/**
 * API Client — admin frontend (cookie-based auth, par kaive_admin_*)
 *
 * - Tokens em httpOnly cookies (set pelo backend em /auth/admin/login)
 * - withCredentials: true → cookies enviados em todo request
 * - Interceptor de 401 → tenta refresh via tokenRefreshQueue (singleton)
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { tokenRefreshQueue } from '@/utils/tokenRefreshQueue';
import { AUTH_ENDPOINTS } from '@/constants/auth';
import { API_BASE_URL } from '@/config/apiBaseUrl';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Kaive-Audience': 'admin',
  },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const success = await tokenRefreshQueue.refresh();
      if (!success) {
        return Promise.reject(error);
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
